import { useMemo } from 'react';
import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery, createQueryHook } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    getOpenApiDeleteCategoryUrl,
    getOpenApiGetCategoryCharacteristicFiltersUrl,
    getOpenApiGetCategoryUrl,
    getOpenApiUpdateCategoryCharacteristicSettingsInBulkUrl,
    getOpenApiUpdateCategoryUrl,
    OpenApiBatchUpdateCategoriesMethod,
    type OpenApiBatchUpdateCategoriesRequest,
    type OpenApiBatchUpdateCategoriesResponseSuccess,
    OpenApiBatchUpdateCategoriesUrl,
    OpenApiCreateCategoryMethod,
    type OpenApiCreateCategoryRequest,
    type OpenApiCreateCategoryResponseSuccess,
    OpenApiCreateCategoryUrl,
    OpenApiDeleteCategoryMethod,
    type OpenApiDeleteCategorySearchParams,
    OpenApiGetCategoriesMethod,
    type OpenApiGetCategoriesResponseSuccess,
    OpenApiGetCategoriesUrl,
    OpenApiGetCategoryCharacteristicFiltersMethod,
    type OpenApiGetCategoryCharacteristicFiltersPathParameters,
    type OpenApiGetCategoryCharacteristicFiltersResponseSuccess,
    OpenApiGetCategoryMethod,
    type OpenApiGetCategoryResponseSuccess,
    OpenApiUpdateCategoryCharacteristicSettingsInBulkMethod,
    type OpenApiUpdateCategoryCharacteristicSettingsInBulkPathParameters,
    type OpenApiUpdateCategoryCharacteristicSettingsInBulkRequest,
    type OpenApiUpdateCategoryCharacteristicSettingsInBulkResponseSuccess,
    OpenApiUpdateCategoryMethod,
    type OpenApiUpdateCategoryRequest,
    type OpenApiUpdateCategoryResponseSuccess,
} from '#shared/api/schema';
import {
    OpenApiGenerateAISeoForCategoryMethod,
    type OpenApiGenerateAISeoForCategoryResponseSuccess,
    OpenApiGenerateAISeoForCategoryUrl,
} from '#shared/api/schema';

const CategoryTag = 'Category' as const;
const CategoryListTag = 'CategoryList' as const;
const CategoryCharacteristicTag = 'CategoryCharacteristic' as const;

export const api = createApi({
    tagTypes: [CategoryTag, CategoryListTag, CategoryCharacteristicTag],
    name: 'entities/category/api/api',
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getAll: builder.query<OpenApiGetCategoriesResponseSuccess, unknown>({
                query: () => {
                    return {
                        url: OpenApiGetCategoriesUrl,
                        method: OpenApiGetCategoriesMethod,
                    };
                },
                providesTags: [CategoryListTag],
                // Добавляем инвалидацию категорий, которые могут обновиться при импорте из внешних источников — 1C/МойСклад/WB
                keepUnusedDataFor: 5,
            }),
            updateCategories: builder.mutation<
                OpenApiBatchUpdateCategoriesResponseSuccess,
                { payload: OpenApiBatchUpdateCategoriesRequest }
            >({
                query: ({ payload }) => {
                    return {
                        url: OpenApiBatchUpdateCategoriesUrl,
                        method: OpenApiBatchUpdateCategoriesMethod,
                        body: payload,
                    };
                },
                invalidatesTags: (_result, _error, args) =>
                    args.payload.map((category) => {
                        return { type: CategoryTag, id: category.id };
                    }),
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: categories } = queryFulfilledResult;

                    api.endpoints.getAll.updateQueryData({}, (draft) => {
                        draft = categories;

                        return draft;
                    });
                },
            }),
            getById: builder.query<OpenApiGetCategoryResponseSuccess, string>({
                query: (id) => {
                    return {
                        url: getOpenApiGetCategoryUrl({ id }),
                        method: OpenApiGetCategoryMethod,
                    };
                },
                keepUnusedDataFor: 2147483,
                providesTags: (result) => [{ type: CategoryTag, id: result?.id }],
            }),
            createCategory: builder.mutation<
                OpenApiCreateCategoryResponseSuccess,
                { payload: OpenApiCreateCategoryRequest }
            >({
                query: ({ payload }) => {
                    return {
                        url: OpenApiCreateCategoryUrl,
                        method: OpenApiCreateCategoryMethod,
                        body: payload,
                    };
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: category } = queryFulfilledResult;

                    api.endpoints.getById.upsertQueryData(category.id, category);
                    api.endpoints.getAll.updateQueryData({}, (draft) => {
                        if (!draft) {
                            return;
                        }

                        draft.push(category);
                    });
                },
            }),
            updateCategory: builder.mutation<
                OpenApiUpdateCategoryResponseSuccess,
                { id: string; payload: OpenApiUpdateCategoryRequest }
            >({
                query: ({ id, payload }) => {
                    return {
                        url: getOpenApiUpdateCategoryUrl({ id }),
                        method: OpenApiUpdateCategoryMethod,
                        body: payload,
                    };
                },
                async onQueryStarted({ id }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: category } = queryFulfilledResult;

                    api.endpoints.getById.upsertQueryData(id, category);
                    api.endpoints.getAll.updateQueryData({}, (draft) => {
                        if (!draft) {
                            return;
                        }

                        draft = draft.map((cat) => (id === cat.id ? category : cat));

                        return draft;
                    });
                },
            }),
            deleteCategory: builder.mutation<void, { id: string; query?: OpenApiDeleteCategorySearchParams }>({
                query: ({ id, query = {} }) => {
                    return {
                        url: getOpenApiDeleteCategoryUrl({ id }),
                        params: query,
                        method: OpenApiDeleteCategoryMethod,
                    };
                },
                invalidatesTags: (_result, _error, { id }) => [
                    {
                        type: CategoryTag,
                        id,
                    },
                ],
                async onQueryStarted({ id }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getAll.updateQueryData({}, (draft) => {
                        if (!draft) {
                            return;
                        }

                        draft = draft.filter((category) => category.id !== id);

                        return draft;
                    });
                },
            }),
            getCharacteristicsById: builder.query<
                OpenApiGetCategoryCharacteristicFiltersResponseSuccess,
                OpenApiGetCategoryCharacteristicFiltersPathParameters
            >({
                query: (pathParams) => {
                    return {
                        url: getOpenApiGetCategoryCharacteristicFiltersUrl(pathParams),
                        method: OpenApiGetCategoryCharacteristicFiltersMethod,
                    };
                },
                providesTags: (_response, _error, { category_id }) => [
                    { type: CategoryCharacteristicTag, id: category_id },
                ],
            }),
            putCharacteristicsSettings: builder.mutation<
                OpenApiUpdateCategoryCharacteristicSettingsInBulkResponseSuccess,
                {
                    params: OpenApiUpdateCategoryCharacteristicSettingsInBulkPathParameters;
                    payload: OpenApiUpdateCategoryCharacteristicSettingsInBulkRequest;
                }
            >({
                query: ({ params, payload }) => {
                    return {
                        url: getOpenApiUpdateCategoryCharacteristicSettingsInBulkUrl(params),
                        method: OpenApiUpdateCategoryCharacteristicSettingsInBulkMethod,
                        body: payload,
                    };
                },
                invalidatesTags: [CategoryCharacteristicTag],
            }),

            generateSeo: builder.mutation<
                OpenApiGenerateAISeoForCategoryResponseSuccess,
                { id: string; user_prompt: string }
            >({
                query: ({ id, user_prompt }) => {
                    return {
                        url: OpenApiGenerateAISeoForCategoryUrl.replace('{id}', encodeURIComponent(id)),
                        method: OpenApiGenerateAISeoForCategoryMethod,
                        body: { user_prompt },
                    };
                },
            }),
        };
    },
});

export const useCategoryGetAll = createQueryHook(function useCategoryGetAll<
    T extends Parameters<typeof api.useGetAllQuery>[0],
>(params: T, withArchive = false) {
    const { data, ...rest } = api.useGetAllQuery(params);
    const filteredItems = useMemo(() => {
        if (!data) {
            return data;
        }

        return data.filter((item) => withArchive || !item.deleted_at);
    }, [data, withArchive]);

    return { data: filteredItems, ...rest };
});

export const useCategoryLazyGetById = api.useLazyGetByIdQuery;
export const useCategoryGetById = api.useGetByIdQuery;
export const useCategoryGetCharacteristicsById = api.useGetCharacteristicsByIdQuery;
export const getCategoryCharacteristicsByIdEndpoint = api.endpoints.getCharacteristicsById;
export const updateCategories = api.endpoints.updateCategories.mutate;
export const putCategoryCharacteristicsSettings = api.endpoints.putCharacteristicsSettings.mutate;

export const getCategoryAll = api.endpoints.getAll.query;
export const createCategory = api.endpoints.createCategory.mutate;
export const updateCategory = api.endpoints.updateCategory.mutate;
export const deleteCategory = api.endpoints.deleteCategory.mutate;

export const invalidateCacheCategoryList = () => api.util.invalidateTags([CategoryListTag]);

export const generateCategorySeo = api.endpoints.generateSeo.mutate;
